import { Component } from '@theme/component';

/**
 * @typedef {Object} WishlistColor
 * @property {string} url
 * @property {string} hexcode
 * @property {string|number} colorId
 * @property {string} colorName
 * @property {string} colorRef
 * @property {string} [description]
 * @property {string} [colorImage]
 * @property {string} [colorBase]
 * @property {string} [colorHandle]
 * @property {string[]} [colorcollection]
 * @property {string[]} [colorfilter]
 * @property {string[]} [coloratmosphere]
 * @property {string} [colorcatalog]
 */

const FAVORITES_CONFIG = {
  wishlistStorageKey: 'color-wishlist',
  maxVisible: 10,
};

const FAVORITES_SELECTORS = {
  container: '[data-color-favorites]',
  toggle: '[data-color-favorites-toggle]',
};

const FAVORITES_CLASSES = {
  card: 'color-browse-grid__card',
  swatch: 'color-browse-grid__swatch',
  info: 'color-browse-grid__info',
  ref: 'color-browse-grid__ref',
  name: 'color-browse-grid__name',
  hidden: 'hidden',
};

const FAVORITES_LABELS = {
  collapsed: 'Load all favorites +',
  expanded: 'Hide favorites −',
};

/**
 * Derive the display color name by stripping the leading code (first word).
 *
 * Mirrors the Liquid logic in `color-browse-grid.liquid` that removes the
 * reference code (e.g. "PE098 California Love" → "California Love").
 *
 * @param {string} rawName
 * @returns {string}
 */
const getDisplayColorName = (rawName) => {
  const trimmed = (rawName || '').trim();

  if (!trimmed) {
    return '';
  }

  const parts = trimmed.split(/\s+/);

  if (parts.length <= 1) {
    return trimmed;
  }

  return parts.slice(1).join(' ');
};

/**
 * Convert a human-readable label to a handle-style string.
 *
 * @param {string} value
 * @returns {string}
 */
const handleize = (value) => {
  if (!value) {
    return '';
  }

  return value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Safely coerce a value into an array of strings.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
const toStringArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  /** @type {string[]} */
  const result = [];

  for (const item of value) {
    if (typeof item === 'string' && item.trim() !== '') {
      result.push(item);
    }
  }

  return result;
};

/**
 * Resolve the default catalog handle for cards that don't have a catalog value.
 *
 * Source of truth is `data-default-catalog` on `<favorite-colors>` (set in Liquid),
 * with a fallback to the Type filter's `data-default-catalog`, and finally a hardcoded
 * "peintagone-collection" default.
 *
 * @param {Element | null} contextEl
 * @returns {string}
 */
const resolveDefaultCatalogHandle = (contextEl) => {
  const hardDefault = 'peintagone-collection';

  if (!(contextEl instanceof Element)) {
    return hardDefault;
  }

  const selfDefault = contextEl.getAttribute('data-default-catalog') || '';
  if (selfDefault) {
    return handleize(selfDefault) || hardDefault;
  }

  const dialogRoot = contextEl.closest('tint-color-picker');
  const scopeRoot = dialogRoot || document;
  const filtersEl = scopeRoot.querySelector('color-browse-filters');
  if (filtersEl instanceof HTMLElement) {
    const filterDefault = filtersEl.dataset.defaultCatalog || '';
    if (filterDefault) {
      return handleize(filterDefault) || hardDefault;
    }
  }

  return hardDefault;
};

/**
 * Build helpers that understand the current product's base rules.
 *
 * Data source is the JSON emitted by the
 * `<script type="application/json" data-base-rules>…</script>`
 * tag in `_tint-color-picker.liquid`.
 *
 * @param {Element | null} contextEl
 * @returns {{
 *   isCompatible: (color: WishlistColor) => boolean;
 *   getEffectiveBaseCode: (color: WishlistColor) => string;
 * }}
 */
const createBaseRulesEvaluator = (contextEl) => {
  if (typeof document === 'undefined') {
    return {
      isCompatible: () => true,
      getEffectiveBaseCode: (color) => handleize(color?.colorBase || ''),
    };
  }

  /** @type {HTMLScriptElement | null} */
  let scriptEl = null;

  if (contextEl instanceof Element) {
    const dialogComponent = contextEl.closest('tint-color-picker');
    const scopeRoot = dialogComponent ? dialogComponent : document;

    scriptEl = scopeRoot.querySelector('script[data-base-rules]');
  } else {
    scriptEl = document.querySelector('script[data-base-rules]');
  }

  if (!(scriptEl instanceof HTMLScriptElement)) {
    return {
      isCompatible: () => true,
      getEffectiveBaseCode: (color) => handleize(color?.colorBase || ''),
    };
  }

  /** @type {unknown} */
  let parsed;

  try {
    const jsonText = scriptEl.textContent || scriptEl.innerHTML || '';
    parsed = jsonText ? JSON.parse(jsonText) : null;
  } catch (error) {
    console.error('base-rules parse error', error);
    return {
      isCompatible: () => true,
      getEffectiveBaseCode: (color) => handleize(color?.colorBase || ''),
    };
  }

  if (!parsed || typeof parsed !== 'object') {
    return {
      isCompatible: () => true,
      getEffectiveBaseCode: (color) => handleize(color?.colorBase || ''),
    };
  }

  const rules = /** @type {{ product_base?: Array<{ id?: unknown; base?: unknown }>; base_exception_1?: Array<{ color_id?: unknown; new_base_id?: unknown }>; base_exception_2?: Array<{ color_id?: unknown }> }} */ (parsed);

  const productBases = Array.isArray(rules.product_base) ? rules.product_base : [];
  const baseOverrides = Array.isArray(rules.base_exception_1) ? rules.base_exception_1 : [];
  const exclusions = Array.isArray(rules.base_exception_2) ? rules.base_exception_2 : [];

  /** @type {Set<string>} */
  const allowedBaseCodes = new Set();
  for (const entry of productBases) {
    if (!entry || typeof entry.base !== 'string') continue;
    const value = entry.base.trim();
    if (!value) continue;
    allowedBaseCodes.add(value);
  }

  /** @type {Map<string, string>} */
  const overrideBaseByColorId = new Map();
  for (const entry of baseOverrides) {
    if (!entry || entry.color_id == null) continue;
    if (typeof entry.new_base_id !== 'string' || !entry.new_base_id) continue;
    overrideBaseByColorId.set(String(entry.color_id), entry.new_base_id);
  }

  /** @type {Set<string>} */
  const excludedColorIds = new Set();
  for (const entry of exclusions) {
    if (!entry || entry.color_id == null) continue;
    excludedColorIds.add(String(entry.color_id));
  }

  if (!allowedBaseCodes.size && !overrideBaseByColorId.size && !excludedColorIds.size) {
    return {
      isCompatible: () => true,
      getEffectiveBaseCode: (color) => handleize(color?.colorBase || ''),
    };
  }

  /**
   * @param {WishlistColor} color
   * @returns {string}
   */
  const getEffectiveBaseCode = (color) => {
    if (!color) {
      return '';
    }

    const colorId = color.colorId != null ? String(color.colorId) : '';

    let effectiveBaseCode = '';

    if (colorId && overrideBaseByColorId.has(colorId)) {
      effectiveBaseCode = overrideBaseByColorId.get(colorId) || '';
    }

    if (!effectiveBaseCode) {
      effectiveBaseCode = handleize(color.colorBase || '');
    }

    return effectiveBaseCode;
  };

  /**
   * @param {WishlistColor} color
   * @returns {boolean}
   */
  const isCompatible = (color) => {
    if (!color) {
      return true;
    }

    const colorId = color.colorId != null ? String(color.colorId) : '';

    if (colorId && excludedColorIds.has(colorId)) {
      return false;
    }

    if (!allowedBaseCodes.size) {
      return true;
    }

    const effectiveBaseCode = getEffectiveBaseCode(color);

    if (!effectiveBaseCode) {
      return true;
    }

    return allowedBaseCodes.has(effectiveBaseCode);
  };

  return { isCompatible, getEffectiveBaseCode };
};

/**
 * Read and normalize wishlist data from localStorage.
 *
 * @returns {WishlistColor[]}
 */
const readWishlist = () => {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return [];
  }

  let rawValue = null;

  try {
    rawValue = window.localStorage.getItem(FAVORITES_CONFIG.wishlistStorageKey);
  } catch (error) {
    console.error('color-wishlist read error', error);
    return [];
  }

  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);

    if (!Array.isArray(parsed)) {
      return [];
    }

    /** @type {WishlistColor[]} */
    const colors = [];

    for (const item of parsed) {
      if (!item || typeof item !== 'object') {
        continue;
      }

      if (!item.colorRef || !item.colorName || !item.hexcode || !item.colorId) {
        continue;
      }

      colors.push(
        /** @type {WishlistColor} */ ({
          url: item.url || '',
          hexcode: item.hexcode,
          colorId: item.colorId,
          colorName: item.colorName,
          colorRef: item.colorRef,
          description: item.description || '',
          colorImage: item.colorImage || '',
          colorBase: item.colorBase || '',
          colorHandle: item.colorHandle || '',
          colorcollection: toStringArray(item.colorcollection),
          colorfilter: toStringArray(item.colorfilter),
          coloratmosphere: toStringArray(item.coloratmosphere),
          colorcatalog: typeof item.colorcatalog === 'string' ? item.colorcatalog : '',
        }),
      );
    }

    return colors;
  } catch (error) {
    console.error('color-wishlist parse error', error);
    return [];
  }
};

/**
 * Create a single favorite color card button.
 *
 * @param {WishlistColor} color
 * @returns {HTMLButtonElement}
 */
const createFavoriteCard = (color) => {
  const button = document.createElement('button');
  const displayColorName = getDisplayColorName(color.colorName || '');

  button.type = 'button';
  button.className = FAVORITES_CLASSES.card;
  button.setAttribute('role', 'radio');
  button.setAttribute('aria-checked', 'false');
  button.setAttribute('aria-label', `${color.colorRef} ${displayColorName}`);

  button.dataset.colorRef = color.colorRef;
  button.dataset.colorName = displayColorName;

  const baseHandle = handleize(color.colorBase || '');
  button.dataset.colorBase = baseHandle || '';

  button.dataset.colorHex = color.hexcode;
  button.dataset.colorId = String(color.colorId);

  if (color.colorHandle) {
    button.dataset.colorHandle = color.colorHandle;
  }

  if (color.colorImage) {
    button.dataset.colorImage = color.colorImage;
  }

  const collectionHandles = (color.colorcollection ?? []).map(handleize).filter(Boolean);
  const filterHandles = (color.colorfilter ?? []).map(handleize).filter(Boolean);

  if (filterHandles.length) {
    button.dataset.colorFilters = filterHandles.join(',');
  }

  if (collectionHandles.length) {
    button.dataset.colorCollections = collectionHandles.join(',');
  }

  // Catalog/type filtering expects handleized values to match the Type dropdown.
  const defaultCatalogHandle = resolveDefaultCatalogHandle(button.closest('favorite-colors'));
  const catalogHandle = handleize(color.colorcatalog || '') || defaultCatalogHandle;
  button.dataset.colorCatalog = catalogHandle;

  const swatch = document.createElement('div');
  swatch.className = FAVORITES_CLASSES.swatch;
  swatch.style.backgroundColor = color.hexcode;

  const info = document.createElement('div');
  info.className = FAVORITES_CLASSES.info;

  const refSpan = document.createElement('span');
  refSpan.className = FAVORITES_CLASSES.ref;
  refSpan.textContent = color.colorRef;

  const nameSpan = document.createElement('span');
  nameSpan.className = FAVORITES_CLASSES.name;
  nameSpan.textContent = displayColorName;

  info.appendChild(refSpan);
  info.appendChild(nameSpan);

  button.appendChild(swatch);
  button.appendChild(info);

  return button;
};

class FavoriteColors extends Component {
  constructor() {
    super();
    /** @type {HTMLButtonElement[] | null} */
    this.cards = null;
    /** @type {HTMLButtonElement | null} */
    this.toggle = null;
    this.collapsedLabel = '';
    this.expandedLabel = '';
    this.hasSearch = false;
    this.hasFilters = false;
    /** @type {Element | Document | null} */
    this.dialogRoot = null;
    this.handleQueryChange = this.handleQueryChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#renderFromStorage();

    const root = this.closest('tint-color-picker');
    if (root instanceof HTMLElement) {
      this.dialogRoot = root;
      root.addEventListener(
        'color-browse-query-change',
        this.handleQueryChange,
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.dialogRoot) {
      this.dialogRoot.removeEventListener(
        'color-browse-query-change',
        this.handleQueryChange,
      );
    }
  }

  /**
   * @param {CustomEvent<{ hasSearch?: boolean; hasFilters?: boolean }>} event
   */
  handleQueryChange(event) {
    const detail = event.detail || {};

    if (typeof detail.hasSearch === 'boolean') {
      this.hasSearch = detail.hasSearch;
    }

    if (typeof detail.hasFilters === 'boolean') {
      this.hasFilters = detail.hasFilters;
    }

    this.updateOverflowVisibility();
  }

  /**
   * Apply overflow / collapsed state for favorites,
   * taking current search and filter state into account.
   */
  updateOverflowVisibility() {
    if (!this.cards || !this.cards.length || !(this.toggle instanceof HTMLButtonElement)) {
      return;
    }

    const cards = this.cards;
    const toggle = this.toggle;
    const hasOverflow = cards.length > FAVORITES_CONFIG.maxVisible;
    const shouldForceExpand = this.hasSearch || this.hasFilters;

    // When there is no overflow, show all cards and hide the toggle.
    if (!hasOverflow) {
      toggle.classList.add(FAVORITES_CLASSES.hidden);
      for (const card of cards) {
        card.classList.remove(FAVORITES_CLASSES.hidden);
      }
      return;
    }

    // When search or filters are active, always show all matching favorites
    // and hide the overflow toggle so results are not clipped.
    if (shouldForceExpand) {
      toggle.classList.add(FAVORITES_CLASSES.hidden);
      toggle.setAttribute('aria-expanded', 'true');

      for (const card of cards) {
        card.classList.remove(FAVORITES_CLASSES.hidden);
      }

      return;
    }

    // No search/filters: respect the user's collapsed/expanded state.
    toggle.classList.remove(FAVORITES_CLASSES.hidden);

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    cards.forEach((card, index) => {
      const shouldHide = !isExpanded && index >= FAVORITES_CONFIG.maxVisible;
      card.classList.toggle(FAVORITES_CLASSES.hidden, shouldHide);
    });

    const collapsedLabel =
      this.collapsedLabel ||
      toggle.dataset.labelCollapsed ||
      FAVORITES_LABELS.collapsed;
    const expandedLabel =
      this.expandedLabel ||
      toggle.dataset.labelExpanded ||
      FAVORITES_LABELS.expanded;

    toggle.textContent = isExpanded ? expandedLabel : collapsedLabel;
  }

  /**
   * Render favorite color cards into the <favorite-colors> element.
   */
  #renderFromStorage() {
    const wishlist = readWishlist();
    const container = this.closest(FAVORITES_SELECTORS.container);

    this.innerHTML = '';

    if (!wishlist.length) {
      if (container instanceof HTMLElement) {
        container.classList.add(FAVORITES_CLASSES.hidden);
      }

      return;
    }

    if (container instanceof HTMLElement) {
      container.classList.remove(FAVORITES_CLASSES.hidden);
    }

    const fragment = document.createDocumentFragment();
    /** @type {HTMLButtonElement[]} */
    const cards = [];

    const baseRules = createBaseRulesEvaluator(this);

    for (const color of wishlist) {
      const card = createFavoriteCard(color);

      const effectiveBaseCode = baseRules.getEffectiveBaseCode(color);
      if (effectiveBaseCode) {
        card.dataset.colorBase = effectiveBaseCode;
      }

      if (!baseRules.isCompatible(color)) {
        card.style.pointerEvents = 'none';
        card.style.opacity = '0.5';
        card.disabled = true;
        card.setAttribute('aria-disabled', 'true');
      }

      cards.push(card);
      fragment.appendChild(card);
    }

    this.appendChild(fragment);

    /** @type {HTMLElement | null} */
    const toggle =
      container instanceof HTMLElement
        ? container.querySelector(FAVORITES_SELECTORS.toggle)
        : null;

    if (!(toggle instanceof HTMLButtonElement)) {
      return;
    }

    this.cards = cards;
    this.toggle = toggle;
    this.collapsedLabel =
      toggle.dataset.labelCollapsed || FAVORITES_LABELS.collapsed;
    this.expandedLabel =
      toggle.dataset.labelExpanded || FAVORITES_LABELS.expanded;

    toggle.setAttribute('aria-expanded', 'false');

    const handleToggleClick = () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const nextExpanded = !expanded;

      toggle.setAttribute('aria-expanded', String(nextExpanded));
      this.updateOverflowVisibility();
    };

    toggle.addEventListener('click', handleToggleClick);

    // Apply initial collapsed / overflow state.
    this.updateOverflowVisibility();
  }
}

customElements.define('favorite-colors', FavoriteColors);
